import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Download,
  ExternalLink,
  Lightbulb,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { fetchDailyListFull, dailyWorksheetPdfUrl } from "../lib/dailyApi";
import { useAuth } from "../contexts/AuthContext";

/**
 * Skill-coloured pill so each card's badge gives a quick visual cue. The
 * Atomic Edu lesson pages use the slate-on-mint convention from the brand
 * palette — we keep that here so the hub feels native to the rest of the
 * site, just with a subtle per-skill background tint.
 */
const SKILL_TINT = {
  "Critical Thinking": "bg-[#96ded1]/35 border-[#96ded1]",
  Collaboration: "bg-[#4a6984]/15 border-[#4a6984]/35",
  Communication: "bg-[#1c2d3d]/10 border-[#1c2d3d]/25",
  "Creativity & Innovation": "bg-amber-100 border-amber-200",
  "Self-Regulation": "bg-rose-100 border-rose-200",
  "Self-Regulation & Well-Being": "bg-rose-100 border-rose-200",
  "Digital Literacy": "bg-sky-100 border-sky-200",
  "Global Citizenship": "bg-emerald-100 border-emerald-200",
  "Learning Skills & Work Habits": "bg-violet-100 border-violet-200",
};

const PAGE_TITLE =
  "12 Free End-of-Year Activities for Ontario Teachers — Atomic Edu";

export default function DailyIndex() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  // Only one card expanded at a time — track its day number, null when
  // none are open. Opening a new card closes the previous one because
  // the same state slot is reassigned.
  const [openDay, setOpenDay] = useState(null);

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  useEffect(() => {
    fetchDailyListFull()
      .then((d) => {
        const items = (d.activities || []).slice().sort((x, y) => x.day - y.day);
        setActivities(items);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="min-h-screen bg-[#f4f5f7] text-[#1c2d3d]"
      data-testid="daily-index-page"
    >
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="daily-home-link"
            className="inline-flex items-center gap-2 font-extrabold tracking-tight hover:opacity-80"
          >
            <img
              src="/brand/atomicedu-logo-mark.png"
              alt="Atomic Edu"
              className="h-9 w-9 object-contain"
            />
            Atomic Edu
          </Link>
          <Link
            to="/pricing"
            data-testid="daily-header-pricing"
            className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-full text-sm font-extrabold text-white/85 hover:text-white hover:bg-white/10"
          >
            Plans
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <header className="max-w-3xl">
          <p className="inline-flex items-center gap-2 font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]">
            <Sparkles className="h-3.5 w-3.5" /> The Daily Free Activity hub
          </p>
          <h1
            data-testid="daily-hub-headline"
            className="mt-4 font-black text-4xl sm:text-5xl lg:text-6xl text-[#1c2d3d] tracking-tighter leading-[1.05]"
          >
            12 Free End-of-Year Activities
          </h1>
          <p
            data-testid="daily-hub-subhead"
            className="mt-5 text-lg text-[#4a6984] font-medium leading-relaxed"
          >
            One for each transferable skill. No sign-up needed — just open
            and use.
          </p>
        </header>

        {loading ? (
          <p
            data-testid="daily-hub-loading"
            className="mt-10 text-[#4a6984]/70 font-medium"
          >
            Loading…
          </p>
        ) : (
          <div
            className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid="daily-hub-grid"
          >
            {activities.map((a) => (
              <ActivityCard
                key={a.day}
                activity={a}
                expanded={openDay === a.day}
                onToggle={() => setOpenDay((d) => (d === a.day ? null : a.day))}
                signedIn={!!user}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Single expandable activity card.                                  */
/* ----------------------------------------------------------------- */

const ActivityCard = ({ activity, expanded, onToggle, signedIn }) => {
  const tint =
    SKILL_TINT[activity.skill] || "bg-[#4a6984]/10 border-[#4a6984]/25";
  const worksheetUrl = dailyWorksheetPdfUrl(activity.day);
  // Signed-in teachers go straight into the generator with the skill
  // pre-filled — no need to re-pitch them on the trial. Signed-out
  // visitors continue down the funnel via /pricing.
  const fullLessonHref = signedIn
    ? `/generator?skill=${encodeURIComponent(activity.skill)}`
    : "/pricing";
  const fullLessonCta = signedIn ? "Build the full lesson" : "Sign in to access";

  return (
    <article
      data-testid={`daily-hub-card-${activity.day}`}
      data-expanded={expanded ? "true" : "false"}
      className={`group rounded-2xl border bg-white transition-all overflow-hidden ${
        expanded
          ? "border-[#96ded1] shadow-xl"
          : "border-[#4a6984]/15 hover:border-[#96ded1]/60 hover:shadow-md"
      }`}
    >
      {/* Tap-target: the entire collapsed card is one button so users can
          poke anywhere — title, hook, badge. */}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`daily-hub-card-toggle-${activity.day}`}
        aria-expanded={expanded}
        aria-controls={`daily-hub-panel-${activity.day}`}
        className="w-full text-left p-6 cursor-pointer"
      >
        <span
          data-testid={`daily-hub-badge-${activity.day}`}
          className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.18em] border text-[#1c2d3d] ${tint}`}
        >
          Day {activity.day} · {activity.skill}
        </span>
        <h2 className="mt-4 font-black text-xl text-[#1c2d3d] tracking-tight leading-tight">
          {activity.title}
        </h2>
        <p className="mt-2 text-sm text-[#4a6984] font-medium leading-relaxed">
          {activity.hook}
        </p>
        <span
          className={`mt-4 inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.16em] ${
            expanded ? "text-[#1c2d3d]" : "text-[#4a6984]/85"
          }`}
        >
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-300 ${
              expanded ? "rotate-180" : "rotate-0"
            }`}
          />
          {expanded ? "Hide activity" : "Show activity"}
        </span>
      </button>

      {/* Expand panel.
          The grid-template-rows 0fr → 1fr trick gives a smooth, content-aware
          height animation without measuring DOM — the parent is a single-row
          grid whose row size is 0 when collapsed and `1fr` when expanded;
          the inner div clips overflow during the transition. */}
      <div
        id={`daily-hub-panel-${activity.day}`}
        role="region"
        aria-hidden={!expanded}
        data-testid={`daily-hub-panel-${activity.day}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-6 pb-6 pt-1 border-t border-[#4a6984]/10 space-y-7">
            {/* How it works */}
            <section>
              <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#4a6984]/75">
                How it works
              </p>
              <ol className="mt-3 space-y-3">
                {(activity.steps || []).map((step, i) => (
                  <li
                    key={i}
                    data-testid={`daily-hub-step-${activity.day}-${i + 1}`}
                    className="flex gap-3"
                  >
                    <span className="shrink-0 h-6 w-6 rounded-full bg-[#96ded1] text-[#1c2d3d] grid place-items-center font-black text-xs">
                      {i + 1}
                    </span>
                    <p className="text-sm text-[#4a6984] font-medium leading-relaxed pt-0.5">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            {/* Teacher tip (subtle inline, not a full card) */}
            {activity.teacher_tip ? (
              <section
                data-testid={`daily-hub-tip-${activity.day}`}
                className="rounded-lg border-l-[3px] border-l-[#96ded1] bg-[#96ded1]/10 px-4 py-3"
              >
                <div className="flex items-start gap-2.5">
                  <Lightbulb className="h-4 w-4 text-[#1c2d3d] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4a6984]/80">
                      Teacher tip
                    </p>
                    <p className="mt-1 text-sm text-[#1c2d3d] font-medium leading-relaxed">
                      {activity.teacher_tip}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}

            {/* Grade adaptations */}
            <section>
              <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#4a6984]/75">
                Adapt it for your grade
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {["4", "5", "6", "7", "8"].map((g) => {
                  const text = (activity.grade_adaptations || {})[g];
                  if (!text) return null;
                  return (
                    <div
                      key={g}
                      data-testid={`daily-hub-grade-${activity.day}-${g}`}
                      className="rounded-md border border-[#4a6984]/15 bg-[#f4f5f7]/60 px-3 py-2.5"
                    >
                      <p className="font-extrabold text-xs text-[#1c2d3d]">
                        Grade {g}
                      </p>
                      <p className="mt-1 text-[12.5px] text-[#4a6984] font-medium leading-snug">
                        {text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Debrief */}
            {activity.debrief_question ? (
              <section
                data-testid={`daily-hub-debrief-${activity.day}`}
                className="rounded-lg bg-[#1c2d3d] text-white px-5 py-4"
              >
                <div className="flex items-start gap-2.5">
                  <MessageCircle className="h-4 w-4 text-[#96ded1] shrink-0 mt-1" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#96ded1]">
                      Debrief question
                    </p>
                    <p className="mt-1.5 text-sm font-bold leading-relaxed">
                      {activity.debrief_question}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}

            {/* Worksheet download + open full activity */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={worksheetUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`daily-hub-worksheet-${activity.day}`}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs font-extrabold bg-[#96ded1] text-[#1c2d3d] hover:bg-white border border-[#96ded1] shadow-sm hover:shadow-md transition-all"
              >
                <Download className="h-3.5 w-3.5" /> Download worksheet (PDF)
              </a>
              <Link
                to={`/daily/day-${activity.day}`}
                data-testid={`daily-hub-fullpage-${activity.day}`}
                className="inline-flex items-center justify-center gap-1.5 text-xs font-extrabold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline"
              >
                Open full activity page <ExternalLink className="h-3 w-3" />
              </Link>
            </div>

            {/* Upgrade cue */}
            <div
              data-testid={`daily-hub-upgrade-${activity.day}`}
              className="border-t border-[#4a6984]/15 pt-5"
            >
              <p className="font-extrabold text-base text-[#1c2d3d] tracking-tight">
                Want the full lesson?
              </p>
              <p className="mt-1.5 text-sm text-[#4a6984] font-medium leading-relaxed">
                Teacher script, slide deck, exit ticket, and observation
                prompts — free with a Google or email sign-in.
              </p>
              <Link
                to={fullLessonHref}
                data-testid={`daily-hub-signin-${activity.day}`}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-extrabold text-[#1c2d3d] hover:underline"
              >
                {fullLessonCta} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};
